import { useEffect, useContext, useCallback } from 'react';
import { roleContext } from '../context/role/context';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

export function useOnboardingTour() {
    const { profile } = useContext(roleContext);

    const startTour = useCallback((force = false) => {
        if (!profile) return;

        const roleId = profile.colaborador ? profile.colaborador.role_id : profile.role_id;
        const pageName = window.location.pathname.replace(/^\/+|\/+$/g, '') || 'hub';
        const storageKey = `onboarding_completed_role_${roleId}_page_${pageName}`;

        // Only auto-run if not forced and not completed yet
        if (!force && localStorage.getItem(storageKey) === 'true') {
            return;
        }

        let steps = [];

        if (pageName === 'stock') {
            if (roleId === 2 || roleId === 1) {
                steps = [
                    {
                        popover: {
                            title: '📦 Catálogo de Productos y Servicios',
                            description: 'En esta sección podrás administrar el inventario, los servicios y los ajustes de stock de tu negocio.',
                            position: 'center'
                        }
                    },
                    {
                        element: '.tab-header:nth-of-type(1)',
                        popover: {
                            title: '🛍️ Productos (Inventariables)',
                            description: 'Aquí gestionas tus productos físicos: existencias, precios de venta, costos de compra, códigos de barras y más.',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '.tab-header:nth-of-type(2)',
                        popover: {
                            title: '🛠️ Servicios',
                            description: 'Registra tus servicios intangibles (mano de obra, consultoría, etc.) que no requieren control de stock físico pero sí facturación.',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '.tab-header:nth-of-type(3)',
                        popover: {
                            title: '📝 Notas de Inventario',
                            description: 'Lleva el registro de entradas y salidas manuales de inventario (por ejemplo, ajustes por merma, pérdidas o carga inicial).',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '.actions-trigger-button',
                        popover: {
                            title: '⚡ Acciones Rápidas',
                            description: 'Haz clic aquí para realizar acciones rápidas: crear un nuevo producto/servicio, configurar impuestos o importar masivamente tu catálogo desde un archivo.',
                            side: 'left',
                            align: 'start'
                        }
                    }
                ];
            }
        } else if (pageName === 'compras') {
            if (roleId === 2 || roleId === 1) {
                steps = [
                    {
                        popover: {
                            title: '🛍️ Gestión de Compras y Gastos',
                            description: 'Aquí registrarás todas las salidas de dinero por compra de mercancías para inventario, gastos generales o documentos soporte.',
                            position: 'center'
                        }
                    },
                    {
                        element: '.tab-header:nth-of-type(1)',
                        popover: {
                            title: '🛒 Módulo de Compra',
                            description: 'Permite abastecer tu inventario seleccionando productos de tu lista y registrando la factura emitida por tu proveedor.',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '.tab-header:nth-of-type(2)',
                        popover: {
                            title: '💸 Gastos Generales',
                            description: 'Ideal para registrar erogaciones que no van al inventario (arriendos, servicios públicos, nómina, etc.).',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '.tab-header:nth-of-type(3)',
                        popover: {
                            title: '📄 Documento Soporte',
                            description: 'Genera el documento electrónico obligatorio para compras realizadas a proveedores no obligados a facturar.',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '.tab-header:nth-of-type(4)',
                        popover: {
                            title: '📋 Historial de Compras',
                            description: 'Consulta, anula o visualiza los detalles de todas las operaciones de compra y egresos previos.',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '.box-tercero-empty-button',
                        popover: {
                            title: '👥 Seleccionar Proveedor',
                            description: 'Antes de guardar, debes asociar un proveedor. Puedes buscar uno existente o crearlo en el momento.',
                            side: 'bottom',
                            align: 'center'
                        }
                    },
                    {
                        element: '#products-table-buy',
                        popover: {
                            title: '🔍 Buscar y Agregar Productos',
                            description: 'Haz clic en el carrito sobre cualquier producto de esta lista para agregarlo a la transacción de compra.',
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        element: '.button-group-content',
                        popover: {
                            title: '⚡ Utilidades de Carga',
                            description: 'Crea nuevos productos rápidamente o sube facturas electrónicas XML recibidas de tus proveedores para cargarlas automáticamente.',
                            side: 'bottom',
                            align: 'end'
                        }
                    }
                ];
            }
        } else {
            if (roleId === 2 || roleId === 1) {
                // Admin / Empresa / SuperAdmin steps
                steps = [
                    {
                        popover: {
                            title: '👋 ¡Te damos la bienvenida!',
                            description: 'Te guiaremos brevemente por las secciones clave para configurar tu negocio y empezar a facturar de inmediato.',
                            position: 'center'
                        }
                    },
                    {
                        element: '#hub-insights',
                        popover: {
                            title: '📈 Métricas en Tiempo Real',
                            description: 'Aquí podrás monitorear el rendimiento de tu negocio: ventas totales, valor facturado y ticket promedio en periodos de Día, Semana o Mes.',
                            side: 'top',
                            align: 'start'
                        }
                    },
                    {
                        element: '#topbar-avatar-btn',
                        popover: {
                            title: '⚙️ Mi Perfil y Empresa',
                            description: 'Haz clic aquí para abrir el menú, ir a "Mi Perfil" y completar los datos de tu empresa (NIT/Régimen, Dirección y teléfono). Es fundamental para la validez de tus documentos y facturas.',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '#sidebar-menu-products',
                        popover: {
                            title: '📦 Productos y Servicios',
                            description: 'Ingresa o importa tus productos y servicios desde este menú. Configura sus precios e impuestos para tener tu catálogo listo.',
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        element: '#sidebar-menu-terceros',
                        popover: {
                            title: '👥 CRM de Terceros',
                            description: 'Registra y organiza a tus clientes y proveedores. Podrás emitir facturas a nombre de clientes específicos y llevar el control de tus deudas.',
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        element: '#sidebar-menu-pos',
                        popover: {
                            title: '🛒 Punto de Venta (POS)',
                            description: '¡El motor de tu negocio! Entra aquí para realizar tus ventas de manera rápida, cobrar en efectivo o tarjeta, e imprimir tickets.',
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        element: '#sidebar-menu-compras',
                        popover: {
                            title: '🛍️ Compras y Gastos',
                            description: 'Registra tus facturas de compra, egresos y gastos operativos aquí para llevar un control financiero exacto de tus costos.',
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        element: '#sidebar-menu-accounting',
                        popover: {
                            title: '📊 Contabilidad Automatizada',
                            description: 'Visualiza en tiempo real los asientos contables, el libro auxiliar y los reportes generados automáticamente por tus ventas y compras.',
                            side: 'right',
                            align: 'start'
                        }
                    },
                    {
                        popover: {
                            title: '🚀 ¡Todo listo!',
                            description: 'Ya tienes claros los pasos iniciales. Configura tus datos y comienza a vender. Puedes repetir esta guía en cualquier momento desde tu Perfil.',
                            position: 'center'
                        }
                    }
                ];
            } else if (roleId === 4) {
                // Accountant / Contador steps
                steps = [
                    {
                        popover: {
                            title: '💼 Centro del Contador',
                            description: 'Te damos la bienvenida a tu espacio de auditoría y gestión de clientes contables.',
                            position: 'center'
                        }
                    },
                    {
                        element: '#topbar-avatar-btn',
                        popover: {
                            title: '✍️ Perfil Profesional',
                            description: 'Haz clic aquí para abrir el menú, ir a "Mi Perfil" y registrar tu Tarjeta Profesional para firmar y validar reportes contables oficiales.',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '#modal-btn-basic-manage-api-keys',
                        popover: {
                            title: '🔑 Gestión de API Keys',
                            description: 'Genera llaves seguras de conexión si necesitas integrar y extraer datos de facturación con otras aplicaciones o ERPs externos.',
                            side: 'bottom',
                            align: 'start'
                        }
                    },
                    {
                        element: '.hub-section',
                        popover: {
                            title: '🏢 Clientes Activos',
                            description: 'Aquí verás el listado de empresas que te han vinculado. Puedes hacer clic en cualquiera de ellas para entrar al POS en modo lectura o auditar sus facturas.',
                            side: 'top',
                            align: 'start'
                        }
                    },
                    {
                        popover: {
                            title: '📈 ¡Listo para auditar!',
                            description: 'Todo listo para apoyar a tus clientes. Recuerda que puedes repetir este recorrido desde la configuración de tu perfil.',
                            position: 'center'
                        }
                    }
                ];
            }
        }

        if (steps.length === 0) return;

        const driverObj = driver({
            showProgress: true,
            steps: steps,
            allowClose: true,
            overlayColor: 'rgba(15, 23, 42, 0.75)',
            progressText: '{{current}} de {{total}}',
            nextBtnText: 'Siguiente →',
            prevBtnText: '← Anterior',
            doneBtnText: 'Finalizar 🎉',
            onDestroyed: () => {
                localStorage.setItem(storageKey, 'true');
            }
        });

        driverObj.drive();
    }, [profile]);

    useEffect(() => {
        if (!profile) return;

        const roleId = profile.colaborador ? profile.colaborador.role_id : profile.role_id;
        const pageName = window.location.pathname.replace(/^\/+|\/+$/g, '') || 'hub';
        const storageKey = `onboarding_completed_role_${roleId}_page_${pageName}`;

        // If already completed and not forced, don't even poll
        if (localStorage.getItem(storageKey) === 'true') {
            return;
        }

        // We check for critical elements based on role and page
        let criticalSelector = '';
        if (pageName === 'stock') {
            criticalSelector = '.tab-header';
        } else if (pageName === 'compras') {
            criticalSelector = '.tab-header';
        } else {
            criticalSelector = (roleId === 2 || roleId === 1) ? '#sidebar-menu-products' : '#modal-btn-manage-api-keys';
        }

        const interval = setInterval(() => {
            const el = document.querySelector(criticalSelector);
            const avatarEl = document.querySelector('#topbar-avatar-btn');
            if (el && (pageName === 'stock' || pageName === 'compras' || avatarEl)) {
                clearInterval(interval);
                startTour(false);
            }
        }, 300);

        // Safety fallback to clear interval after 10 seconds
        const timeout = setTimeout(() => {
            clearInterval(interval);
        }, 10000);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
        };
    }, [profile, startTour]);

    return { startTour };
}
